import { copy } from '@ohear/shared'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Modal,
  Spinner,
  Textarea,
  useToast,
} from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AccountTabs } from '../../features/account/AccountTabs'
import { useAccountProfile } from '../../lib/api/account'
import {
  useRecentSessions,
  useRequestAccountDelete,
  useRequestDataExport,
  useRevokeOtherSessions,
} from '../../lib/api/account'
import {
  useUnreadNotificationCount,
  useUpdateNotificationPreferences,
} from '../../lib/api/notifications'
import { useReplayTour } from '../../lib/api/onboarding'
import { useAuth } from '../../lib/auth'
import { useT } from '../../lib/i18n/use-t'
import { AccountHeader } from './profile'

export const Route = createFileRoute('/account/security')({
  component: SecurityPage,
})

function SecurityPage() {
  const t = useT()
  const { user } = useAuth()
  const profile = useAccountProfile()
  const sessions = useRecentSessions()
  const revoke = useRevokeOtherSessions()
  const exportData = useRequestDataExport()
  const requestDelete = useRequestAccountDelete()
  const replayTour = useReplayTour()
  const navigate = useNavigate()
  const toast = useToast()
  const updatePrefs = useUpdateNotificationPreferences()
  const unread = useUnreadNotificationCount({ pollMs: 0 })

  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  // Local toggle state for the digest preference. Hydrates from profile once
  // it loads, then mirrors local input until the next mutation succeeds.
  const initialDigest =
    (profile.data as { email_digest_enabled?: boolean } | undefined)?.email_digest_enabled ?? true
  const [digestOn, setDigestOn] = useState<boolean>(initialDigest)
  useEffect(() => {
    const next = (profile.data as { email_digest_enabled?: boolean } | undefined)
      ?.email_digest_enabled
    if (next !== undefined) setDigestOn(next)
  }, [profile.data])

  if (!user) {
    return (
      <Section>
        <Card>
          <p className="m-0">{t(copy.common.SIGN_IN_REQUIRED.security)}</p>
        </Card>
      </Section>
    )
  }

  const isActor = profile.data?.primary_role === 'actor'
  const sessionList = sessions.data?.sessions ?? []

  const submitDelete = () => {
    requestDelete.mutate(deleteReason.trim() || null, {
      onSuccess: () => {
        toast.show({
          severity: 'success',
          title: 'Delete request received',
          description: 'Our team will follow up by email within one business day.',
        })
        setConfirmingDelete(false)
      },
      onError: (err) => {
        toast.show({
          severity: 'error',
          title: 'Could not submit request',
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      },
    })
  }

  return (
    <Section profile={profile.data}>
      <AccountTabs active="security" isActor={Boolean(isActor)}>
        <div className="flex flex-col gap-4">
          <Card title="Login method">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px] m-0">
              <div>
                <dt className="text-[12px] text-[var(--text-2)] m-0">Method</dt>
                <dd className="m-0 mt-0.5 font-medium">Magic link</dd>
              </div>
              <div>
                <dt className="text-[12px] text-[var(--text-2)] m-0">Email</dt>
                <dd className="m-0 mt-0.5 font-medium break-all">{user.email}</dd>
              </div>
            </dl>
          </Card>

          <Card
            title="Recent sessions"
            actions={
              <Button
                size="sm"
                variant="ghost"
                loading={revoke.isPending}
                onClick={() =>
                  revoke.mutate(undefined, {
                    onSuccess: (data) => {
                      toast.show({
                        severity: 'success',
                        title: `${data.revoked} session${data.revoked === 1 ? '' : 's'} signed out`,
                      })
                    },
                  })
                }
              >
                Sign out everywhere else
              </Button>
            }
          >
            {sessions.isLoading ? (
              <div className="flex items-center gap-2 text-[var(--text-2)]">
                <Spinner /> Loading sessions...
              </div>
            ) : sessionList.length === 0 ? (
              <EmptyState
                title="No sessions on file"
                description="We can't show session history yet."
              />
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col">
                {sessionList.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-2 text-[13px] border-b border-[var(--border)] py-2 last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{s.user_agent ?? 'Unknown device'}</div>
                      <div className="text-[12px] text-[var(--text-2)]">
                        {s.ip ?? 'unknown ip'} - {new Date(s.signed_in_at).toLocaleString()}
                      </div>
                    </div>
                    {s.current ? <Badge variant="green">this device</Badge> : null}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Data">
            <div className="flex flex-col gap-2 text-[13px]">
              <p className="text-[var(--text-2)] m-0">
                Request a one-time export of every take, contract, and invoice tied to your account.
                We'll email a download link when it's ready (usually under an hour).
              </p>
              <div>
                <Button
                  variant="ghost"
                  loading={exportData.isPending}
                  onClick={() =>
                    exportData.mutate(undefined, {
                      onSuccess: (data) => {
                        toast.show({
                          severity: 'success',
                          title: 'Export queued',
                          description: `We'll email ${data.email} when it's ready.`,
                        })
                      },
                      onError: (err) => {
                        toast.show({
                          severity: 'error',
                          title: 'Could not queue export',
                          description: err instanceof Error ? err.message : 'Unknown error',
                        })
                      },
                    })
                  }
                >
                  Export my data
                </Button>
              </div>
            </div>
          </Card>

          <Card title="Onboarding tour">
            <div className="flex flex-col gap-2 text-[13px]">
              <p className="text-[var(--text-2)] m-0">
                Replay the first-run product tour. We'll send you to the home page so the
                role-appropriate steps line up with the right elements.
              </p>
              <div>
                <Button
                  variant="ghost"
                  loading={replayTour.isPending}
                  onClick={() =>
                    replayTour.mutate(undefined, {
                      onSuccess: () => {
                        toast.show({
                          severity: 'success',
                          title: 'Tour reset',
                          description: 'Returning you to the dashboard…',
                        })
                        void navigate({ to: '/' })
                      },
                      onError: (err) => {
                        toast.show({
                          severity: 'error',
                          title: 'Could not reset tour',
                          description: err instanceof Error ? err.message : 'Unknown error',
                        })
                      },
                    })
                  }
                >
                  Replay onboarding tour
                </Button>
              </div>
            </div>
          </Card>

          <Card
            title="Notifications"
            actions={
              <Link
                to="/account/notifications"
                className="text-[12px] text-[var(--accent)] no-underline hover:underline"
              >
                View all{unread.data?.unread_count ? ` (${unread.data.unread_count} unread)` : ''}
              </Link>
            }
          >
            <div className="flex flex-col gap-3 text-[13px]">
              <Checkbox
                checked={digestOn}
                onChange={(e) => {
                  const next = e.target.checked
                  setDigestOn(next)
                  updatePrefs.mutate(
                    { email_digest_enabled: next },
                    {
                      onSuccess: () =>
                        toast.show({
                          severity: 'success',
                          title: next ? 'Daily digest enabled' : 'Daily digest disabled',
                        }),
                      onError: (err) => {
                        // Roll the toggle back if the mutation failed.
                        setDigestOn(!next)
                        toast.show({
                          severity: 'error',
                          title: 'Could not update preferences',
                          description: err instanceof Error ? err.message : 'Unknown error',
                        })
                      },
                    },
                  )
                }}
                label="Daily email digest"
                description="A morning summary of pending takes, pickup requests, and payment milestones. We never email you about things you've already read."
                disabled={updatePrefs.isPending}
              />
            </div>
          </Card>

          <Card title="Delete account">
            <div className="flex flex-col gap-2 text-[13px]">
              <p className="text-[var(--text-2)] m-0">
                A delete request opens a ticket with our support team. We'll confirm by email before
                any data is removed.
              </p>
              <div>
                <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
                  Request account deletion
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </AccountTabs>

      <Modal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        title="Request account deletion"
        size="md"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setConfirmingDelete(false)}
              disabled={requestDelete.isPending}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={submitDelete} loading={requestDelete.isPending}>
              Send request
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-[13px] m-0">
            Tell us briefly why you're leaving (optional). Our team will follow up by email before
            anything is deleted.
          </p>
          <Textarea
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            rows={4}
            placeholder="Reason for leaving (optional)"
            disabled={requestDelete.isPending}
          />
        </div>
      </Modal>
    </Section>
  )
}

function Section({
  children,
  profile,
}: {
  children: React.ReactNode
  profile?: import('../../lib/api/account').AccountProfile
}) {
  return (
    <section className="flex flex-col gap-4">
      <AccountHeader profile={profile} />
      {children}
    </section>
  )
}
